'use client';

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { usePathname, useSearchParams } from 'next/navigation';
import { useCompanyPreview } from '@/new_components/searchresults/useCompanyPreview';
import { canonicalFilters, filtersToUrl, normalizeFilterParams, normalizeSearchSort, sanitizeFinancialYear, searchParamsWithoutPreview } from '@/lib/search-filters';
import { aiSearchKey } from '@/lib/ai-search';
import { getAiSearchProgress, resetAiSearchProgress, useAiSearchProgress } from '@/lib/ai-search-progress';
import { CompanySearchResult, searchCompanies } from '@/lib/actions/company-search';
import CompanySearchSidebar from '@/new_components/searchresults/CompanySearchSidebar';
import type { FilterParams } from '@/lib/search-filter-params';
import { activeFilterCount, activeFilterValues, hasAnyFilter } from '@/new_components/searchresults/activeFilters';
import { filterLocationsForCountry } from '@/new_components/searchresults/searchConstants';
import { SaveSearchDialog } from '@/new_components/saved-searches/SaveSearchDialog';
import { useMarkOpened, useSavedSearches } from '@/hooks/use-saved-searches';
import { useTranslation } from 'react-i18next';
import { trackSearchPagination, trackSearchPerformed, trackSearchSortChange } from '@/lib/analytics';
import { isAdmin, isPro } from '@/lib/permissions';
import { type FilterTier, tierForSession } from '@/lib/search-gating';
import { useLocale } from '@/hooks/use-locale';
import { useRecordRecentSearch } from '@/hooks/use-recent-activity';
import { useSession } from '@/hooks/use-session';
import { interactionState } from '@/components/page-view-logger';
import { buildInitialFilters, exportLogBody, filtersToSearchParams, submittedFilterCount } from './searchPageFilters';
import type { SearchPageClientProps } from './searchPageTypes';
import { SearchResultsMain } from './SearchResultsMain';

export default function SearchPageClient({
  naceCodes: initialNaceCodes,
  cityList: initialCityList,
  tagList: initialTagList,
  searchResponse: initialSearchResponse,
  searchSkipped,
  initialOffset,
  initialSort,
  initialLimit,
  initialFinancialYear,
  initialTier,
  presetFilters,
  listSlug,
  resultsHeader,
}: SearchPageClientProps) {
  const { t } = useTranslation();
  const urlSearchParams = useSearchParams();
  const renderedSearchParams = searchParamsWithoutPreview(urlSearchParams).toString();
  const searchParams = useMemo(
    () => new URLSearchParams(renderedSearchParams),
    [renderedSearchParams],
  );
  const { previewEuId, openPreview, closePreview } = useCompanyPreview();
  const pathname = usePathname();
  const aiProgress = useAiSearchProgress();
  const aiIsLoading = !listSlug && (aiProgress.phase === 'interpreting' || aiProgress.phase === 'loading');
  // Which rendered search an AI search produced, so its search_query log row
  // can say so (the progress itself is reset as soon as the results render).
  const aiOriginRef = useRef<{ key: string; query: string } | null>(null);
  useEffect(() => {
    const progress = getAiSearchProgress();
    const key = aiSearchKey(new URLSearchParams(renderedSearchParams));
    if (progress.phase === 'loading' && progress.targetKey === key) {
      aiOriginRef.current = { key, query: progress.query };
      resetAiSearchProgress();
    }
  }, [renderedSearchParams]);
  const { locale } = useLocale();
  const { data: session, status: sessionStatus } = useSession();
  const recordRecentSearch = useRecordRecentSearch();
  const isAdminUser = isAdmin(session);
  // Export and enrich are Pro or Admin only. Export is shown to everyone (with
  // a "Pro feature" prompt for the rest); the enrich buttons are hidden for the
  // rest.
  const isProOrAdmin = isAdminUser || isPro(session);
  // Filter gating runs off the server-known tier until the client session
  // lands, so the locks never contradict what the server will actually
  // enforce on the next request.
  const tier: FilterTier =
    sessionStatus === 'loading' && initialTier
      ? initialTier
      : tierForSession(session);

  // Lazy-load filter data if not provided by SSR. Fire three independent
  // fetches so the small NACE list lands in the sidebar immediately
  // (~50ms) instead of being held up behind the keyword scan over
  // yearly_metadata (~3-5s).
  const [naceCodes, setNaceCodes] = useState(initialNaceCodes);
  const [cityList, setCityList] = useState(initialCityList);
  const [tagList, setTagList] = useState(initialTagList);
  const filtersFetchedRef = useRef(false);
  useEffect(() => {
    if (filtersFetchedRef.current) return;
    if (
      initialNaceCodes.length > 0 &&
      initialCityList.length > 0 &&
      initialTagList.length > 0
    )
      return;
    filtersFetchedRef.current = true;
    if (initialNaceCodes.length === 0) {
      fetch('/api/search/filters/nace')
        .then((res) => res.json())
        .then((data) => {
          if (data.naceCodes) setNaceCodes(data.naceCodes);
        })
        .catch(() => {});
    }
    if (initialTagList.length === 0) {
      fetch('/api/search/filters/keywords')
        .then((res) => res.json())
        .then((data) => {
          if (data.tags) setTagList(data.tags);
        })
        .catch(() => {});
    }
  }, [initialNaceCodes, initialCityList, initialTagList]);

  // Default closed so the mobile fullscreen overlay doesn't flash on SSR
  // hydration. Desktop opens it on mount via the effect below.
  const [filtersOpen, setFiltersOpen] = useState(false);
  const filtersInitRef = useRef(false);
  useEffect(() => {
    if (filtersInitRef.current) return;
    filtersInitRef.current = true;
    if (window.innerWidth >= 1024) setFiltersOpen(true);
  }, []);

  // Mobile flow: the header's filter button dispatches
  // `firmendata:open-filters` (see HeaderLogo), which opens the filter panel
  // fullscreen on mobile. The sidebar's own submit handler closes it again.
  useEffect(() => {
    const onOpen = () => setFiltersOpen(true);
    window.addEventListener('firmendata:open-filters', onOpen);
    return () => window.removeEventListener('firmendata:open-filters', onOpen);
  }, []);

  // Lock body scroll while the mobile fullscreen overlay is open so iOS Safari
  // doesn't leak touch scrolling to the page underneath. Desktop is unaffected
  // because the sidebar is in normal flow there.
  useEffect(() => {
    if (!filtersOpen) return;
    if (window.innerWidth >= 1024) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, [filtersOpen]);

  // Mobile shows results as cards capped at 10 per page. If the initial limit
  // (URL or default) is larger, re-issue the search at 10 once on mount so the
  // pagination steps line up with the visible cards.
  const mobileLimitInitRef = useRef(false);
  useEffect(() => {
    if (mobileLimitInitRef.current) return;
    if (window.innerWidth >= 1024) return;
    mobileLimitInitRef.current = true;
    if (currentLimit > 10) {
      handleLimitChange(10);
    }
    // handleLimitChange is stable per the deps below; we intentionally only
    // want this to run once on mount.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const [isLoading, setIsLoading] = useState(false);

  // Client-side search state (initialized from SSR response)
  const [searchResponse, setSearchResponse] = useState(initialSearchResponse);
  // The search params the response on screen belongs to. The URL changes
  // before a new response arrives (client search replaces it first; a soft
  // navigation re-renders before the reset effect runs), so the search log
  // must wait until both agree — it used to record the previous count.
  const [responseParams, setResponseParams] = useState(renderedSearchParams);
  const [currentOffset, setCurrentOffset] = useState(initialOffset);
  const [currentSort, setCurrentSort] = useState(initialSort);
  const [currentLimit, setCurrentLimit] = useState(initialLimit);
  const [currentFinancialYear, setCurrentFinancialYear] =
    useState(initialFinancialYear);

  // Desktop uses a fixed full-viewport split layout, so we lock body scroll
  // to keep the page from double-scrolling. Mobile uses the natural page flow
  // (the results list scrolls with the page), so we must NOT lock body there
  // or the page becomes unscrollable.
  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (window.innerWidth < 1024) return;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = '';
    };
  }, []);

  const initialFilters: FilterParams = useMemo(
    () => buildInitialFilters(searchParams, presetFilters, listSlug, pathname, locale),
    [searchParams, presetFilters, listSlug, pathname, locale],
  );

  // Active filter count for the header badge, derived from the filter object
  // rather than a hand-kept list — that list had already drifted from
  // FilterParams. `q` is excluded: it is the search term, shown in the header
  // input, not a badge. Broadcast as a CustomEvent so the global HeaderLogo
  // can listen without importing search-page state.
  const filterCount = useMemo(
    () => activeFilterCount(initialFilters, tier),
    [initialFilters, tier],
  );

  useEffect(() => {
    window.dispatchEvent(
      new CustomEvent('firmendata:filter-count', { detail: filterCount }),
    );
  }, [filterCount]);

  // Cities are fetched per country and re-fetched when the scope changes, so
  // the dropdown never offers a city the current country cannot return.
  // Unlike the state list (a 16-entry constant the client can filter), there
  // are 13.6K cities and which country each belongs to is only knowable in
  // SQL. Kept out of the one-shot filter block above, which is fire-once by
  // design; declared here because it reads the parsed URL filters.
  const cityCountry = initialFilters.country;
  useEffect(() => {
    if (initialCityList.length > 0) return;
    let cancelled = false;
    const qs = cityCountry ? `?country=${cityCountry}` : '';
    fetch(`/api/search/filters/cities${qs}`)
      .then((res) => res.json())
      .then((data) => {
        if (!cancelled && data.cities) setCityList(data.cities);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [cityCountry, initialCityList]);

  // Fetch default results on mount only when SSR deliberately skipped the
  // search. A real search that returned 0 results must NOT fall back to the
  // default list — it should show "no results".
  const initialFetchDone = useRef(false);
  useEffect(() => {
    if (initialFetchDone.current) return;
    if (searchSkipped) {
      initialFetchDone.current = true;
      setIsLoading(true);
      searchCompanies({ limit: initialLimit, offset: 0, sort: initialSort })
        .then((res) => setSearchResponse(res))
        .catch(() => {})
        .finally(() => setIsLoading(false));
    }
  }, [searchSkipped, initialLimit, initialSort]);

  // Log search query to /api/log/search after the result count settles.
  // Gated on real interaction for guests (mirrors PageViewLogger) so crawlers
  // and link unfurlers — which fetch HTML but don't move a mouse — never log.
  const lastLoggedSearchRef = useRef<string | null>(null);
  useEffect(() => {
    if (sessionStatus === 'loading') return;
    if (typeof navigator !== 'undefined' && navigator.webdriver) return;

    if (!hasAnyFilter(initialFilters, tier)) return;
    if (responseParams !== renderedSearchParams) return;

    const url = `/search?${renderedSearchParams}`;
    if (lastLoggedSearchRef.current === url) return;

    const isGuest = sessionStatus !== 'authenticated';

    const doLog = () => {
      lastLoggedSearchRef.current = url;
      if (sessionStatus === 'authenticated') {
        recordRecentSearch({
          url,
          filters: { ...initialFilters },
          resultCount: searchResponse.total,
        });
      }
      fetch('/api/log/search', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          // Every applied filter, derived from the filter object — the old
          // hand-written subset covered 13 of 28, so searches narrowed only
          // by e.g. Rechtsform or membership were invisible to analytics.
          ...activeFilterValues(initialFilters, tier),
          filterKeys: Object.keys(activeFilterValues(initialFilters, tier)),
          sort: currentSort,
          resultCount: searchResponse.total,
          zeroResults: searchResponse.total === 0,
          url,
          ...(aiOriginRef.current?.key ===
            aiSearchKey(new URLSearchParams(renderedSearchParams)) && {
            ai: true,
            aiQuery: aiOriginRef.current.query.slice(0, 200),
          }),
        }),
        keepalive: true,
      }).catch(() => {});
    };

    let timer: ReturnType<typeof setTimeout> | null = null;
    let interval: ReturnType<typeof setInterval> | null = null;

    if (isGuest) {
      timer = setTimeout(() => {
        if (interactionState.happened) {
          doLog();
        } else {
          let checks = 0;
          interval = setInterval(() => {
            checks++;
            if (interactionState.happened) {
              clearInterval(interval!);
              doLog();
            } else if (checks >= 6) {
              clearInterval(interval!);
            }
          }, 2000);
        }
      }, 3000);
    } else {
      doLog();
    }

    return () => {
      if (timer) clearTimeout(timer);
      if (interval) clearInterval(interval);
    };
  }, [
    searchParams,
    renderedSearchParams,
    responseParams,
    initialFilters,
    searchResponse.total,
    currentSort,
    sessionStatus,
    tier,
    recordRecentSearch,
  ]);

  // Abort controller for in-flight searches
  const abortRef = useRef<AbortController | null>(null);
  // Filters behind the currently displayed results. executeSearch records the
  // exact FilterParams it ran with, so the Excel export can re-fetch the same
  // result set (just more rows) regardless of how the search was triggered.
  const lastFiltersRef = useRef<FilterParams>(initialFilters);
  // Track whether the first client-side search has happened (for pushState vs replaceState)
  const hasClientSearched = useRef(false);

  // Sync from SSR when URL changes externally (e.g. browser back/forward, or a
  // new search from the header). Skip only for the default search, whose empty
  // response is a placeholder the on-mount client fetch fills in — for a real
  // search we sync even when it returned 0 results, so the previous results
  // clear instead of lingering.
  //
  // Note: returning to the bare default search from an already-open search page
  // (the "Search" nav link, the header search bar submitted empty) is handled
  // by a full page reload — `hardNavigate` on the link, `goToKeywordlessSearch`
  // in LandingSearch — so this effect doesn't need to untangle stale client
  // state from a same-page soft navigation.
  useEffect(() => {
    if (searchSkipped) return;
    setSearchResponse(initialSearchResponse);
    setResponseParams(renderedSearchParams);
    setCurrentOffset(initialOffset);
    setCurrentSort(initialSort);
    setCurrentLimit(initialLimit);
    setCurrentFinancialYear(initialFinancialYear);
    // The results now on screen were produced by the URL's filters, so the
    // export re-fetch must use those — not whatever executeSearch last ran.
    // initialFilters stays out of the deps on purpose: its identity also
    // changes on every shallow pushState executeSearch performs, and firing
    // there would clobber fresh client results with the stale SSR response.
    lastFiltersRef.current = initialFilters;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    searchSkipped,
    initialSearchResponse,
    initialOffset,
    initialSort,
    initialLimit,
    initialFinancialYear,
  ]);

  // Client-side search: fetch via API route, update URL without full page reload
  const executeSearch = useCallback(
    async (
      filters: FilterParams,
      overrides: {
        sort: string;
        offset: number;
        limit: number;
      },
    ) => {
      // Abort previous in-flight request
      if (abortRef.current) abortRef.current.abort();
      const controller = new AbortController();
      abortRef.current = controller;

      // Next patches native history to update useSearchParams, which changes
      // initialFilters and triggers the sidebar sync after a chip removal.
      // Update URL (shallow — no server re-render).
      // Use pushState on first client search so back-button returns to the
      // previous page, then replaceState for subsequent filter changes.
      const url = filtersToUrl(filters, overrides, locale);
      if (!hasClientSearched.current) {
        hasClientSearched.current = true;
        window.history.pushState(null, '', url);
      } else {
        window.history.replaceState(null, '', url);
      }

      // Remember the filters behind this result set for the export re-fetch.
      lastFiltersRef.current = filters;

      setIsLoading(true);
      try {
        const params = filtersToSearchParams(filters, naceCodes, overrides);
        const response = await searchCompanies({
          ...params,
          ...(listSlug && { listSlug, locale }),
        });
        if (!controller.signal.aborted) {
          setSearchResponse(response);
          setResponseParams(
            searchParamsWithoutPreview(
              new URL(url, window.location.origin).searchParams,
            ).toString(),
          );
          setCurrentOffset(overrides.offset);
          setCurrentSort(overrides.sort);
          setCurrentLimit(overrides.limit);
          setCurrentFinancialYear(sanitizeFinancialYear(filters.financialYear));
        }
      } catch (err: unknown) {
        if (!(err instanceof Error && err.name === 'AbortError')) {
          console.error('Search failed:', err);
        }
      } finally {
        if (!controller.signal.aborted) {
          setIsLoading(false);
        }
      }
    },
    [naceCodes, listSlug, locale],
  );

  // The header's flag selector (LandingSearch) applies its choice here, so a
  // country change runs through the same client search path as any sidebar
  // filter — instant results, shallow URL update, sidebar state synced from
  // the URL. It acts on the filters behind the results on screen; staged
  // sidebar edits stay staged.
  const applyCountry = useCallback(
    (country: string) => {
      const prev = lastFiltersRef.current;
      if ((prev.country ?? '') === country) return;
      executeSearch(
        {
          ...prev,
          country,
          Bundesland: filterLocationsForCountry(prev.Bundesland, country),
          // Cities cannot be checked client-side (13.6K of them, country
          // known only in SQL), so narrowing to a specific country clears
          // them: "Switzerland + Hamburg" is a guaranteed empty result the
          // user did not ask for. Widening to global keeps them — every
          // selected city is still reachable there.
          city: country ? [] : prev.city,
        },
        { sort: currentSort, offset: 0, limit: currentLimit },
      );
    },
    [executeSearch, currentSort, currentLimit],
  );

  useEffect(() => {
    const onSetCountry = (e: Event) =>
      applyCountry((e as CustomEvent<string>).detail ?? 'DE');
    window.addEventListener('firmendata:set-country', onSetCountry);
    return () =>
      window.removeEventListener('firmendata:set-country', onSetCountry);
  }, [applyCountry]);

  const handleSubmit = useCallback(
    (filters: FilterParams) => {
      const filterCount = submittedFilterCount(filters);
      trackSearchPerformed({
        query: filters.q,
        filterCount,
        resultCount: searchResponse.total,
      });

      const sortOverride =
        (filters.q || filters.personName) &&
        (currentSort === 'revenue-desc' || !searchParams.get('sort'))
          ? 'relevance'
          : currentSort;
      executeSearch(filters, {
        sort: sortOverride,
        offset: 0,
        limit: currentLimit,
      });

      if (typeof window !== 'undefined' && window.innerWidth < 1024) {
        setFiltersOpen(false);
      }
    },
    [
      executeSearch,
      searchResponse.total,
      currentSort,
      currentLimit,
      searchParams,
    ],
  );

  // ── saved searches ────────────────────────────────────────────────────────
  const { items: savedSearches } = useSavedSearches();
  const markSavedSearchOpened = useMarkOpened();
  const [saveDialogOpen, setSaveDialogOpen] = useState(false);
  const [pendingSave, setPendingSave] = useState<FilterParams | null>(null);

  /**
   * Save applies the filters as well.
   *
   * The sidebar hands over its *staged* state, which need not match the
   * results on screen. Running the search as part of saving is what removes
   * the ambiguity — after this the visible results are the saved search,
   * and the dialog's chip preview showed exactly what was stored.
   */
  const handleSaveSearch = useCallback(
    (filters: FilterParams) => {
      handleSubmit(filters);
      setPendingSave(filters);
      setSaveDialogOpen(true);
    },
    [handleSubmit],
  );

  const handleLoadSavedSearch = useCallback(
    (id: number) => {
      const saved = savedSearches.find((s) => s.id === id);
      if (!saved) return;
      // The badge counts new matches since the user last *looked*, so it
      // only resets here — not when the card merely renders.
      markSavedSearchOpened(id);
      const filters = normalizeFilterParams(saved.filters);
      // `sort` holds this page's combined form ("revenue-desc"), the same
      // string executeSearch and the backend both already understand — so
      // sort_direction stays unused here rather than being re-derived.
      executeSearch(filters, {
        sort: normalizeSearchSort(
          saved.sort || currentSort,
          filters.q || filters.personName,
        ),
        offset: 0,
        limit: currentLimit,
      });
    },
    [savedSearches, markSavedSearchOpened, executeSearch, currentSort, currentLimit],
  );

  const savedSearchOptions = useMemo(
    () =>
      savedSearches.map((s) => ({
        id: s.id,
        name: s.name,
        filters: normalizeFilterParams(s.filters),
      })),
    [savedSearches],
  );

  const isCurrentSearchSaved = useMemo(() => {
    const current = canonicalFilters(
      initialFilters as unknown as Record<string, unknown>,
    );
    return savedSearches.some((s) => canonicalFilters(s.filters) === current);
  }, [savedSearches, initialFilters]);

  const pendingSaveQuery = useMemo(() => {
    if (!pendingSave) return null;
    return filtersToSearchParams(pendingSave, naceCodes, {
      sort: currentSort,
      offset: 0,
      limit: currentLimit,
    }) as unknown as Record<string, unknown>;
  }, [pendingSave, naceCodes, currentSort, currentLimit]);

  const handleSortChange = useCallback(
    (newSort: string) => {
      trackSearchSortChange(newSort);
      executeSearch(initialFilters, {
        sort: newSort,
        offset: 0,
        limit: currentLimit,
      });
    },
    [
      executeSearch,
      initialFilters,
      currentLimit,
    ],
  );

  const handleLimitChange = useCallback(
    (newLimit: number) => {
      executeSearch(initialFilters, {
        limit: newLimit,
        offset: 0,
        sort: currentSort,
      });
    },
    [
      executeSearch,
      initialFilters,
      currentSort,
    ],
  );

  const handleOffsetChange = useCallback(
    (newOffset: number) => {
      trackSearchPagination(newOffset);
      executeSearch(initialFilters, {
        offset: newOffset,
        sort: currentSort,
        limit: currentLimit,
      });
    },
    [
      executeSearch,
      initialFilters,
      currentSort,
      currentLimit,
    ],
  );

  const handleRefresh = useCallback(() => {
    executeSearch(initialFilters, {
      sort: currentSort,
      offset: currentOffset,
      limit: currentLimit,
    });
  }, [executeSearch, initialFilters, currentSort, currentOffset, currentLimit]);

  // Overlay freshly-enriched employee counts onto the displayed rows. The
  // counts come straight from the enrich response (parsed from employee_data);
  // mv_search is a materialized view we can't write per-click, so this is an
  // in-memory patch. A new search replaces results entirely, falling back to
  // the MV value until the next scheduled MV refresh.
  const handleEmployeeCountsUpdated = useCallback(
    (counts: Record<string, number>) => {
      if (Object.keys(counts).length === 0) return;
      setSearchResponse((prev) => ({
        ...prev,
        results: prev.results.map((r) =>
          counts[r.euId] != null ? { ...r, employees: counts[r.euId] } : r,
        ),
      }));
    },
    [],
  );

  // Fetch up to `limit` rows for the Excel export, reusing the exact filters,
  // sort and financial year behind the currently displayed results. Returns
  // the rows only — it never touches the table state or the URL.
  const handleFetchForExport = useCallback(
    async (limit: number): Promise<CompanySearchResult[]> => {
      const params = filtersToSearchParams(lastFiltersRef.current, naceCodes, {
        sort: currentSort,
        offset: 0,
        limit,
      });
      const response = await searchCompanies(params);
      return response.results;
    },
    [
      naceCodes,
      currentSort,
    ],
  );

  // Log Excel-export usage to /api/log/export so it surfaces on /admin/logs.
  // `stage` is 'click' (button pressed — any user, incl. guests and non-Pro
  // users, before the Pro gate) or 'completed' (file generated). The active
  // filters mirror the search-query log so admins can see what was exported.
  const logExport = useCallback(
    (stage: 'click' | 'completed', extra?: Record<string, unknown>) => {
      const url = `/search?${renderedSearchParams}`;
      const body = exportLogBody(stage, extra, url, isProOrAdmin, searchParams, initialFilters, currentSort, { total: searchResponse.total });
      fetch('/api/log/export', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
        keepalive: true,
      }).catch(() => {});
    },
    [
      searchParams,
      renderedSearchParams,
      isProOrAdmin,
      initialFilters,
      currentSort,
      searchResponse.total,
    ],
  );

  return (
    <div className="search-warm-bg relative lg:fixed lg:left-[var(--app-sidebar-width,0px)] lg:right-0 lg:w-[calc(100%-var(--app-sidebar-width,0px))] lg:top-[var(--header-height,95px)] lg:bottom-[calc(var(--footer-height,60px)+env(safe-area-inset-bottom,0px))] flex flex-col lg:flex-row min-h-0 w-full max-w-full lg:overflow-hidden bg-background">
      {/* The sr-only H1 labels the page for assistive tech on /search. When a
        * `resultsHeader` is provided (e.g. /listen/[slug] passes a visible H1
        * in its hero), we skip it to keep a single H1 per page for SEO. */}
      {resultsHeader == null && (
        <h1 className="sr-only">{t('search.sidebar.title')}</h1>
      )}

      <CompanySearchSidebar
        naceCodes={naceCodes}
        cityList={cityList}
        tagList={tagList}
        onSubmit={handleSubmit}
        sort={currentSort}
        offset={currentOffset}
        limit={currentLimit}
        totalLimit={currentLimit}
        onTotalLimitChange={handleLimitChange}
        showFinancialYear
        total={searchResponse.total}
        onOffsetChange={handleOffsetChange}
        onCountryChange={applyCountry}
        filterParams={initialFilters}
        isOpen={filtersOpen}
        onClose={() => setFiltersOpen(false)}
        mobileFullscreen
        tier={tier}
        hideCompanySearch
        onSaveSearch={session ? handleSaveSearch : undefined}
        savedSearches={savedSearchOptions}
        onLoadSavedSearch={handleLoadSavedSearch}
        isCurrentSearchSaved={isCurrentSearchSaved}
      />

      {pendingSave && pendingSaveQuery && (
        <SaveSearchDialog
          open={saveDialogOpen}
          onOpenChange={setSaveDialogOpen}
          filters={pendingSave}
          query={pendingSaveQuery}
          sort={currentSort}
        />
      )}

      <SearchResultsMain
        t={t}
        resultsHeader={resultsHeader}
        aiIsLoading={aiIsLoading}
        aiProgress={aiProgress}
        listSlug={listSlug}
        initialFilters={initialFilters}
        tier={tier}
        handleSubmit={handleSubmit}
        previewEuId={previewEuId}
        openPreview={openPreview}
        searchResponse={searchResponse}
        currentOffset={currentOffset}
        currentLimit={currentLimit}
        currentSort={currentSort}
        handleSortChange={handleSortChange}
        handleOffsetChange={handleOffsetChange}
        handleLimitChange={handleLimitChange}
        handleRefresh={handleRefresh}
        currentFinancialYear={currentFinancialYear}
        isProOrAdmin={isProOrAdmin}
        session={session}
        handleFetchForExport={handleFetchForExport}
        logExport={logExport}
        handleEmployeeCountsUpdated={handleEmployeeCountsUpdated}
        isLoading={isLoading}
        closePreview={closePreview}
      />
    </div>
  );
}
